import {
  FORECAST_INFLUENCE,
  LIBRARY_INFLUENCE,
  PRODUCT_TYPES,
  getFollowUpFields,
  getLengthOptions,
  getPatternSubtypes,
} from "../../constants/imageGeneratorDictionaries";
import { collectionBuildBorderClassName } from "./collectionBuildStyles";
import { helperTextClassName, RequiredMark } from "./ImageGeneratorControls";

export { collectionBuildBorderClassName } from "./collectionBuildStyles";
export { RequiredMark, helperTextClassName };

export type RangePlanItem = {
  id: string;
  productType: string;
  quantity: number;
};

export type SelectOption = {
  value: string;
  label: string;
  imageUrl?: string;
};

export const LOCKED_GENRE = "Fashion";
export const MAX_RANGE_PRODUCTS = 20;
export const MAX_RANGE_PLAN_ROWS = 20;
export const RANGE_PLAN_PAGE_SIZE = 7;
export const ADD_SLOT = "add" as const;
export const EMPTY_SLOT = "empty" as const;
export const DEFAULT_QUANTITY = 0;
export const MAX_QUANTITY = 99;
/** Joi max for session `patternTrendIds` (BE samples further for prompt). */
export const COLLECTION_BUILD_PATTERN_TREND_IDS_MAX = 12;
/** Joi max for session `selectedAssetStems`. */
export const COLLECTION_BUILD_SELECTED_ASSET_STEMS_MAX = 8;
export const RANGE_LABEL_COL = "7.3rem";
export const RANGE_PRODUCT_COL = "7.5rem";
export const RANGE_TYPE_ROW_HEIGHT = "3.5rem";
export const RANGE_QTY_ROW_HEIGHT = "3.5rem";

export type RangePlanSlot = RangePlanItem | typeof ADD_SLOT;
export type RangePlanDisplaySlot = RangePlanSlot | typeof EMPTY_SLOT;

// Query selections use live catalog/seasons/denim APIs.
// All Starting Direction pills stay visible; unavailable ones stay disabled.
export const COLLECTION_DIRECTION_OPTIONS = [
  "Macro-theme",
  "Denim",
  "Capsules",
] as const;
export type StartingDirection = (typeof COLLECTION_DIRECTION_OPTIONS)[number];

export const COLLECTION_RECENTS_EMPTY = "Recent collections will show up here.";

export const NO_STARTING_DIRECTIONS_MESSAGE =
  "No starting directions for this season";
export const DIRECTION_UNAVAILABLE_HINT = "Not available for this season";
/** Starting Direction / Select Direction section helper. */
export const SELECT_DIRECTION_HELPER =
  "Where would you like to get started from?";

/**
 * Denim only for Western RTW (mirrors IG `isDenimEligibleCategory` —
 * kept local so Collection Build does not depend on IG module edits).
 * CB category values are "Ethnic RTW" / "Western RTW".
 */
export const isCollectionBuildDenimEligibleCategory = (
  category?: string
): boolean => String(category || "").trim() === "Western RTW";

/** UI label only — request/response still use Ethnic RTW / Western RTW. */
export const categoryLabel = (category: string) =>
  category.replace(/\s*RTW\s*$/i, "").trim() || category;

/**
 * Enabled Starting Direction values. Unavailable options stay visible but
 * disabled (except Denim, which is omitted from the pill list when the
 * category is not Western RTW). Capsules enables when catalog.capsules has
 * themes.
 */
export const availableCollectionDirections = ({
  hasReportThemes,
  hasCapsules,
  denimAvailable,
}: {
  hasReportThemes: boolean;
  hasCapsules: boolean;
  denimAvailable: boolean;
}): StartingDirection[] => {
  const options: StartingDirection[] = [];
  if (hasReportThemes) {
    options.push("Macro-theme");
  }
  if (denimAvailable) {
    options.push("Denim");
  }
  if (hasCapsules) {
    options.push("Capsules");
  }
  return options;
};

export const directionSkipsMacroTheme = (direction: string) =>
  direction === "Denim";

export const PATTERN_TREND_CHOICES = [
  { value: "choose", label: "I will choose" },
  { value: "recommend", label: "You suggest" },
] as const;
export type PatternTrendChoice =
  (typeof PATTERN_TREND_CHOICES)[number]["value"];

/** Pattern vs Surface Trend — same product labels as Image Generator. */
export const TREND_SOURCE_CHOICES = [
  { value: "pattern", label: "Pattern" },
  { value: "surface", label: "Surface Trend" },
] as const;
export type TrendSourceChoice = (typeof TREND_SOURCE_CHOICES)[number]["value"];

export const TREND_SOURCE_HELPER =
  "Pick a print from this story, or a surface-report slide.";

export type ColorPaletteSwatch = {
  hex: string;
  name?: string;
  tier?: "major" | "minor";
  pantone?: string | null;
};

export const trimmedPantoneField = (
  pantone?: string | null
): Pick<ColorPaletteSwatch, "pantone"> | Record<string, never> => {
  const trimmed = (pantone || "").trim();
  return trimmed ? { pantone: trimmed } : {};
};

export type ColorPaletteOption = {
  id: string;
  label: string;
  colors: string[];
  swatches?: ColorPaletteSwatch[];
};

export const paletteHasMixedTiers = (swatches?: ColorPaletteSwatch[]) =>
  Boolean(swatches?.some(item => item.tier === "major")) &&
  Boolean(swatches?.some(item => item.tier === "minor"));

/**
 * CB palette display order: major (or unknown) first, then minor.
 * Relative order within each tier is preserved. Circle size stays uniform.
 */
export const orderPaletteDotsMajorFirst = (
  colors: string[],
  swatches?: ColorPaletteSwatch[]
): { colors: string[]; swatches?: ColorPaletteSwatch[] } => {
  if (!colors.length) {
    return { colors, swatches };
  }
  const entries = colors.map((hex, index) => ({
    hex,
    swatch: swatches?.[index],
    index,
  }));
  entries.sort((a, b) => {
    const aMinor = a.swatch?.tier === "minor" ? 1 : 0;
    const bMinor = b.swatch?.tier === "minor" ? 1 : 0;
    if (aMinor !== bMinor) {
      return aMinor - bMinor;
    }
    return a.index - b.index;
  });
  const nextColors = entries.map(entry => entry.hex);
  if (!swatches?.length) {
    return { colors: nextColors };
  }
  return {
    colors: nextColors,
    swatches: entries.map(
      entry => entry.swatch || ({ hex: entry.hex } as ColorPaletteSwatch)
    ),
  };
};

export const paletteSwatchTitle = (
  hex: string,
  swatch?: ColorPaletteSwatch,
  showTier = false
) => {
  const pantoneField = trimmedPantoneField(swatch?.pantone);
  return [
    swatch?.name,
    pantoneField.pantone ?? null,
    showTier ? swatch?.tier : null,
    hex,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const isCapsuleStartingDirection = (value?: string | null) =>
  /capsule/i.test(String(value || ""));

const isUserAuthoredPaletteId = (id?: string | null) => {
  const value = String(id || "").trim();
  return value.startsWith("custom-") || value.startsWith("upload-");
};

/**
 * Capsule Color Palette caption and swatch hover.
 * `capsuleName` is the catalog capsule theme `name`.
 * Empty means the caller keeps the colour-story title, pantone, and hex.
 * Applies when starting direction is Capsules, or `paletteId` is that capsule's theme id.
 */
export const capsulePaletteDisplayName = ({
  startingDirection,
  capsuleName,
  paletteId,
  themeId,
  paletteIsCatalog = true,
}: {
  startingDirection?: string | null;
  capsuleName?: string | null;
  paletteId?: string | null;
  /** Selected catalog capsule id (`capsules[].id`). */
  themeId?: string | null;
  paletteIsCatalog?: boolean;
}): string => {
  const name = String(capsuleName || "").trim();
  if (!name || isUserAuthoredPaletteId(paletteId)) {
    return "";
  }
  const id = String(paletteId || "").trim();
  const selectedThemeId = String(themeId || "").trim();
  const paletteIsCapsule = Boolean(selectedThemeId) && id === selectedThemeId;
  if (!isCapsuleStartingDirection(startingDirection) && !paletteIsCapsule) {
    return "";
  }
  if (!paletteIsCatalog && !paletteIsCapsule) {
    return "";
  }
  return name;
};

export const MAX_CUSTOM_PALETTE_COLORS = 4;
export const DENIM_MAX_CUSTOM_PALETTE_COLORS = 3;
export const DEFAULT_CUSTOM_COLOR = "#8EBFDC";
export const EMPTY_CUSTOM_SLOT_COLOR = "#E8E8E8";

export const PATTERN_TREND_HELPER =
  "Warming up! Got a favourite pattern trend from this story, or want me to suggest?";
/** Western Pattern Trend — copy before catalog micro-theme pills (not hardcoded names). */
export const WESTERN_PATTERN_NARROW_HELPER = "Let's narrow it down.";
export const MICRO_THEME_HELPER =
  "Great choice for a brand like Forever New! Select one or more micro-themes from this report — that is enough to continue.";
export const PALETTE_UPLOAD_HELPER =
  "*Upload reference photo to capture your palette";
export const DENIM_PALETTE_UPLOAD_HELPER =
  "*Upload reference photo to capture your palette";
export const DENIM_PALETTE_MAKE_OWN_HINT =
  "You can also make your own from selecting these.";
export const CUSTOM_PALETTE_MAX_HELPER = "Up to 4 colors";
export const GARMENT_FORMAT_HELPER =
  "What format would you like the pattern to appear on the garments?";
export const MOODBOARD_HELPER =
  "Awesome! Would you like a complimenting mood board too?";

/** Skip-moodboard confirm — exact product copy. */
export const SKIP_MOODBOARD_CONFIRM_TITLE =
  "Are you sure you want to jump straight ahead in rendering the collection’s looks?";
export const SKIP_MOODBOARD_CONFIRM_BODY =
  "Starting with a mood board helps to visualize the concept direction!";

export const COLLECTION_BUILD_MOODBOARD_AFTER_FIRST =
  "Done! Here’s your moodboard!";
export const COLLECTION_BUILD_GEN_AFTER_SECOND = "Sure, let’s have another go!";
export const COLLECTION_BUILD_GEN_BEFORE_THIRD = "What should we adjust?";
export const COLLECTION_BUILD_GEN_AFTER_THIRD =
  "Here's another version - let's see if this lands better.";
export const COLLECTION_BUILD_GEN_AFTER_FOURTH =
  "Refined it again. Getting closer?";
export const COLLECTION_BUILD_GEN_AFTER_FIFTH =
  "Final take for now - happy with this one, or want to relook at the brief? Definitely want to nail it the next time!";

/**
 * Status copy after a completed generation. Moodboard-specific first-gen
 * text is omitted for looks (`kind: "look"`).
 */
export const collectionBuildAfterGenerationCopy = (
  completedCount: number,
  kind: "moodboard" | "look"
): string => {
  if (completedCount <= 0) {
    return "";
  }
  if (completedCount === 1) {
    return kind === "moodboard" ? COLLECTION_BUILD_MOODBOARD_AFTER_FIRST : "";
  }
  if (completedCount === 2) {
    return COLLECTION_BUILD_GEN_AFTER_SECOND;
  }
  if (completedCount === 3) {
    return COLLECTION_BUILD_GEN_AFTER_THIRD;
  }
  if (completedCount === 4) {
    return COLLECTION_BUILD_GEN_AFTER_FOURTH;
  }
  return COLLECTION_BUILD_GEN_AFTER_FIFTH;
};

export type CollectionBuildRegenPromptCopy = {
  title: string;
  required: boolean;
};

/** Regen composer copy from completed session generations (next = count + 1). */
export const collectionBuildRegenPromptCopy = (
  completedCount: number
): CollectionBuildRegenPromptCopy => {
  const nextGeneration = completedCount + 1;
  if (nextGeneration <= 1) {
    return { title: "", required: false };
  }
  if (nextGeneration === 2) {
    return {
      title: COLLECTION_BUILD_GEN_AFTER_SECOND,
      required: false,
    };
  }
  if (completedCount >= 5) {
    return {
      title: COLLECTION_BUILD_GEN_AFTER_FIFTH,
      required: true,
    };
  }
  return {
    title: COLLECTION_BUILD_GEN_BEFORE_THIRD,
    required: true,
  };
};

/** 3rd generation onward (and later after the 5th) require a change prompt. */
export const collectionBuildRegenRequiresPrompt = (completedCount: number) =>
  collectionBuildRegenPromptCopy(completedCount).required;

export const COLLECTION_GARMENT_FORMATS = [
  "Print",
  "Embroidery",
  "Graphic",
] as const;

export const GARMENT_FORMAT_RANKS = [
  "primary",
  "secondary",
  "tertiary",
] as const;

export type GarmentFormatRank = (typeof GARMENT_FORMAT_RANKS)[number];

export const GARMENT_FORMAT_RANK_LABELS: Record<GarmentFormatRank, string> = {
  primary: "Primary",
  secondary: "Secondary",
  tertiary: "Tertiary",
};

/** Figma closed-select empty state. */
export const GARMENT_FORMAT_SELECT_PLACEHOLDER =
  "( Primary | Secondary | Tertiary )";

/** Figma open-panel helper — ranks follow check order. */
export const GARMENT_FORMAT_MULTI_HELPER =
  "Select options in order of priority.";

export type SilhouetteGarmentFormatEntry = {
  format: string;
  /** Check-order rank: first checked = primary, then secondary, tertiary. */
  rank: GarmentFormatRank;
  patternFormat: string;
  embroideryType: string;
};

/** Looser shape from look payloads / seeds before normalize. */
export type SilhouetteGarmentFormatInput = {
  format?: string;
  rank?: GarmentFormatRank | string;
  patternFormat?: string;
  embroideryType?: string;
};

/** @deprecated Prefer patternFormatsForGarment / getPatternSubtypes (IG dictionaries). */
export const PATTERN_FORMATS_BY_GARMENT: Record<string, readonly string[]> = {
  Print: getPatternSubtypes("Print"),
  Embroidery: getPatternSubtypes("Embroidery"),
  Graphic: getPatternSubtypes("Graphic"),
};

export const PREFERENCE_PAGE_SIZE = 10;
export const LOOK_BOOK_PAGE_SIZE = 4;

/** How many look cards to show on the current Look Book page (1–4). */
export const lookBookPageSlotCount = (lookCountOnPage: number) =>
  Math.min(LOOK_BOOK_PAGE_SIZE, Math.max(1, lookCountOnPage));

/** Total looks implied by a range-plan breakdown (sum of quantities). */
export const lookCountFromRangeBreakdown = (
  breakdown?: ReadonlyArray<{ quantity?: number }> | null
) =>
  (breakdown || []).reduce(
    (sum, item) => sum + Math.max(0, Number(item.quantity) || 0),
    0
  );

export type PreferenceOption = {
  id: string;
  imageUrl: string;
  label: string;
};

/** Pattern Story (`pattern_trend`) with its preference tiles (`pattern_tile` assets). */
export type PreferenceGroup = {
  id: string;
  label: string;
  items: PreferenceOption[];
};

/** Denim material/wash tile in Design Details (same catalog assets as the form). */
export type CollectionBuildDenimTile = {
  id: string;
  label: string;
  imageUrl: string;
};

export type SilhouetteSketchAsset = {
  stem: string;
  url: string;
  available: boolean;
  label?: string;
  /** Editorial report title from silhouettes-catalog (when slide name is generic). */
  reportTitle?: string;
  /** 0-based slider page index from the report deck, when available. */
  sliderImageIndex?: number | null;
};

export const EMPTY_SKETCH_ASSETS: SilhouetteSketchAsset[] = [];

const GENERIC_SILHOUETTE_LABEL = /^(silhouettes?|flat sketches?)$/i;

export const isGenericSilhouetteLabel = (label?: string | null) => {
  const trimmed = String(label || "").trim();
  return !trimmed || GENERIC_SILHOUETTE_LABEL.test(trimmed);
};

/**
 * Caption under a selected-sketch thumb. Prefers a real page/report name;
 * falls back to a page index when the only label is the generic category.
 * Returns null only when a single generic selection has no page index.
 */
export const silhouetteSketchCaption = (
  asset: SilhouetteSketchAsset,
  orderIndex: number,
  selectedCount: number
): string | null => {
  for (const candidate of [asset.label, asset.reportTitle]) {
    const trimmed = String(candidate || "").trim();
    if (trimmed && !isGenericSilhouetteLabel(trimmed)) {
      return trimmed;
    }
  }
  if (
    asset.sliderImageIndex != null &&
    Number.isFinite(asset.sliderImageIndex)
  ) {
    const page =
      asset.sliderImageIndex >= 0
        ? asset.sliderImageIndex + 1
        : asset.sliderImageIndex;
    return String(page);
  }
  if (selectedCount <= 1) {
    return null;
  }
  return String(orderIndex + 1);
};

export const patternTrendLabel = (choice: PatternTrendChoice | "") => {
  if (choice === "choose") {
    return "Choose";
  }
  if (choice === "recommend") {
    return "Recommend";
  }
  return "";
};

export type CollectionBuildRangeBreakdown = {
  productType: string;
  quantity: number;
};

export type CollectionBuildDetailRow = {
  label: string;
  value: string;
};

/** Selected Surface Trend slide id and name. Labels still show the id. */
export type CollectionBuildSurfaceTrend = {
  id: string;
  name: string;
};

export type CollectionBuildResultDetails = {
  genre: string;
  season: string;
  market: string;
  category: string;
  rangePlanTotal: number;
  rangePlanBreakdown: CollectionBuildRangeBreakdown[];
  startingDirection: string;
  macroTheme: string;
  patternTrend: string;
  /** Joined display labels for selected micro-themes (legacy single string). */
  microTheme: string;
  /** Individual micro-theme display names (all selected capsules). */
  microThemes?: string[];
  /** Denim wash display label (resolved from denimWashId). */
  wash: string;
  /** Catalog material tile for Denim Design Details; omitted on other paths. */
  denimMaterialTile?: CollectionBuildDenimTile;
  /** Catalog wash tile for Denim Design Details; omitted on other paths. */
  denimWashTile?: CollectionBuildDenimTile;
  /** All unique denim materials selected across Look Details styles. */
  denimMaterialTiles?: CollectionBuildDenimTile[];
  /** All unique denim washes selected across Look Details styles. */
  denimWashTiles?: CollectionBuildDenimTile[];
  garmentFormat: string;
  patternFormat: string;
  paletteHexes: string[];
  palettes: ColorPaletteOption[];
  preferences: PreferenceOption[];
  /** Selected surface slides. Design Details keeps the slide id, not the name. */
  surfaceTrends?: CollectionBuildSurfaceTrend[];
  /**
   * Legacy per-look Select Product / silhouette summary rows.
   * Kept on the snapshot for session hydrate compat; not shown in Design Details
   * (too verbose when many looks exist).
   */
  silhouetteRows?: CollectionBuildDetailRow[];
  sketchAssets?: SilhouetteSketchAsset[];
  frozenRows: CollectionBuildDetailRow[];
  skipMoodboard?: boolean;
  persistPayload?: Record<string, unknown>;
};

export type CollectionBuildDetailsInput = Omit<
  CollectionBuildResultDetails,
  "frozenRows"
>;

const microThemeLabelsForRows = (
  input: CollectionBuildDetailsInput
): string[] => {
  const fromList = (input.microThemes || [])
    .map(item => String(item || "").trim())
    .filter(Boolean);
  if (fromList.length) {
    return fromList;
  }
  const joined = String(input.microTheme || "").trim();
  if (!joined) {
    return [];
  }
  return joined
    .split(",")
    .map(part => part.trim())
    .filter(Boolean);
};

const collectionBuildFrozenRows = (
  input: CollectionBuildDetailsInput
): CollectionBuildDetailRow[] => {
  const microLabels = microThemeLabelsForRows(input);
  return (
    [
      { label: "Genre", value: input.genre },
      { label: "Season", value: input.season },
      { label: "Market", value: input.market },
      // Display Ethnic/Western; stored details.category stays Ethnic RTW/Western RTW.
      { label: "Category", value: categoryLabel(input.category) },
      {
        label: "Range Plan",
        value: input.rangePlanTotal
          ? `${input.rangePlanTotal} Style${
              input.rangePlanTotal === 1 ? "" : "s"
            }`
          : "",
      },
      input.macroTheme
        ? {
            label: /capsule/i.test(input.startingDirection)
              ? "Capsule"
              : "Macro Theme",
            value: input.macroTheme,
          }
        : { label: "Starting Direction", value: input.startingDirection },
      { label: "Pattern Trend", value: input.patternTrend },
      {
        label: microLabels.length > 1 ? "Micro-themes" : "Micro-theme",
        value: microLabels.join(", "),
      },
      {
        label: "Wash",
        // Denim wash is a labeled image tile; keep text only as a fallback.
        value:
          input.denimWashTile || (input.denimWashTiles || []).length
            ? ""
            : input.wash,
      },
      { label: "Garments Format", value: input.garmentFormat },
      // Per-look silhouette / Look Details rows are intentionally omitted —
      // Design Details stays high-level when collections have many looks.
    ] as CollectionBuildDetailRow[]
  ).filter(row => Boolean(row.value));
};

export const snapshotCollectionBuildDetails = (
  input: CollectionBuildDetailsInput
): CollectionBuildResultDetails => {
  const microThemes = microThemeLabelsForRows(input);
  const silhouetteRows = (input.silhouetteRows || [])
    .map(row => ({
      label: String(row.label || "").trim(),
      value: String(row.value || "").trim(),
    }))
    .filter(row => row.label && row.value);
  return {
    genre: input.genre,
    season: input.season,
    market: input.market,
    category: input.category,
    rangePlanTotal: input.rangePlanTotal,
    rangePlanBreakdown: (input.rangePlanBreakdown || []).map(item => ({
      ...item,
    })),
    startingDirection: input.startingDirection,
    macroTheme: input.macroTheme,
    patternTrend: input.patternTrend,
    microTheme: microThemes.join(", ") || input.microTheme,
    microThemes,
    wash: input.wash || "",
    denimMaterialTile: input.denimMaterialTile
      ? { ...input.denimMaterialTile }
      : undefined,
    denimWashTile: input.denimWashTile ? { ...input.denimWashTile } : undefined,
    denimMaterialTiles: (() => {
      const tiles = (input.denimMaterialTiles || [])
        .filter(tile => tile?.id)
        .map(tile => ({ ...tile }));
      if (tiles.length) {
        return tiles;
      }
      return input.denimMaterialTile
        ? [{ ...input.denimMaterialTile }]
        : undefined;
    })(),
    denimWashTiles: (() => {
      const tiles = (input.denimWashTiles || [])
        .filter(tile => tile?.id)
        .map(tile => ({ ...tile }));
      if (tiles.length) {
        return tiles;
      }
      return input.denimWashTile ? [{ ...input.denimWashTile }] : undefined;
    })(),
    garmentFormat: input.garmentFormat,
    patternFormat: input.patternFormat,
    paletteHexes: [...(input.paletteHexes || [])],
    palettes: (input.palettes || []).map(item => ({
      ...item,
      colors: [...(item.colors || [])],
      swatches: item.swatches?.map(swatch => ({ ...swatch })),
    })),
    preferences: (input.preferences || []).map(item => ({ ...item })),
    surfaceTrends: (input.surfaceTrends || [])
      .map(item => ({
        id: String(item.id || "").trim(),
        name: String(item.name || "").trim(),
      }))
      .filter(item => item.id && item.name && item.name !== item.id),
    silhouetteRows,
    sketchAssets: (input.sketchAssets || []).map(item => ({ ...item })),
    frozenRows: collectionBuildFrozenRows({
      ...input,
      microTheme: microThemes.join(", ") || input.microTheme,
      microThemes,
      silhouetteRows,
      rangePlanBreakdown: input.rangePlanBreakdown || [],
      paletteHexes: input.paletteHexes || [],
      palettes: input.palettes || [],
      preferences: input.preferences || [],
    }),
    skipMoodboard: input.skipMoodboard,
    persistPayload: input.persistPayload,
  };
};

export type CollectionBuildMoodboardVersion = {
  id: string;
  imageUrl: string;
  outputId?: number;
  /**
   * Structured SVG download URL (svg_hybrid). Absent for AI boards —
   * SVG menu then falls back to PNG wrapped in SVG.
   */
  svgUrl?: string | null;
  /** Persisted output review.approvedAt — each render can be approved independently. */
  approvedAt?: string | null;
};

export type CollectionBuildLookVersion = {
  id: string;
  frontUrl: string;
  backUrl: string;
  outputId?: number;
  /** Output row status — used to keep placeholders while a view is still generating. */
  status?: string;
  /** Persisted output review.approvedAt — each render can be approved independently. */
  approvedAt?: string | null;
};

/** Show a look pair only when both front and back image URLs exist. */
export const lookVersionPairReady = (
  version?: CollectionBuildLookVersion | null
): boolean =>
  Boolean(
    String(version?.frontUrl || "").trim() &&
      String(version?.backUrl || "").trim()
  );

export type CollectionBuildSheetSlot = {
  id: string;
  slotId: string;
  label: string;
  imageUrl: string;
  /** Per-slide structured SVG when CB engine is svg_hybrid. */
  svgUrl?: string | null;
};

/** One design-sheet generation pack (may contain look + apparel + print slides). */
export type CollectionBuildSheetVersion = {
  id: string;
  /** Cover / first slot — used for rail thumbs. */
  imageUrl: string;
  outputId?: number;
  approvedAt?: string | null;
  slots: CollectionBuildSheetSlot[];
};

export type CollectionBuildLook = {
  id: string;
  number: number;
  productType: string;
  styleIndex: number;
  versions: CollectionBuildLookVersion[];
};

export const MOCK_LOOK_IMAGE_PAIRS = [
  {
    front:
      "https://images.unsplash.com/photo-1572804013309-59a88b7e92f1?auto=format&fit=crop&w=800&q=80",
    back: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?auto=format&fit=crop&w=800&q=80",
  },
  {
    front:
      "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80",
    back: "https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?auto=format&fit=crop&w=800&q=80",
  },
  {
    front:
      "https://images.unsplash.com/photo-1596755094514-f87e34085b2c?auto=format&fit=crop&w=800&q=80",
    back: "https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?auto=format&fit=crop&w=800&q=80",
  },
  {
    front:
      "https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?auto=format&fit=crop&w=800&q=80",
    back: "https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?auto=format&fit=crop&w=800&q=80",
  },
  {
    front:
      "https://images.unsplash.com/photo-1542272604-787c3835535d?auto=format&fit=crop&w=800&q=80",
    back: "https://images.unsplash.com/photo-1541099649105-f69ad21f3246?auto=format&fit=crop&w=800&q=80",
  },
  {
    front:
      "https://images.unsplash.com/photo-1564257631407-4deb1f99d4a5?auto=format&fit=crop&w=800&q=80",
    back: "https://images.unsplash.com/photo-1551488831-00ddcb6c6bd3?auto=format&fit=crop&w=800&q=80",
  },
  {
    front:
      "https://images.unsplash.com/photo-1583496661160-fb5886a0aaaa?auto=format&fit=crop&w=800&q=80",
    back: "https://images.unsplash.com/photo-1577900232427-18219b9166a0?auto=format&fit=crop&w=800&q=80",
  },
  {
    front:
      "https://images.unsplash.com/photo-1551028719-00167b16eac5?auto=format&fit=crop&w=800&q=80",
    back: "https://images.unsplash.com/photo-1591047139829-d91aecb6caea?auto=format&fit=crop&w=800&q=80",
  },
] as const;

export const lookImagePairAt = (index: number) =>
  MOCK_LOOK_IMAGE_PAIRS[
    ((index % MOCK_LOOK_IMAGE_PAIRS.length) + MOCK_LOOK_IMAGE_PAIRS.length) %
      MOCK_LOOK_IMAGE_PAIRS.length
  ];

const DESIGN_SHEET_THEMES = [
  {
    title: "Botanical Wrap",
    paper: "#F7F4EE",
    ink: "#2F2F2F",
    muted: "#8A847C",
    print: "#D7E2D2",
    motif: "#7E9274",
    photo: "#E4DDD4",
  },
  {
    title: "Ink Floral",
    paper: "#F6F3EF",
    ink: "#2C3340",
    muted: "#7A7F88",
    print: "#D5DCE6",
    motif: "#6B7C93",
    photo: "#E2E4E8",
  },
  {
    title: "Desert Clay",
    paper: "#F8F3EC",
    ink: "#3A2E28",
    muted: "#8B7C70",
    print: "#E8D5C3",
    motif: "#C4785A",
    photo: "#E9DFD4",
  },
  {
    title: "Coastal Fade",
    paper: "#F4F6F4",
    ink: "#2A3640",
    muted: "#748088",
    print: "#D3E2E8",
    motif: "#7BA3C9",
    photo: "#DEE4E8",
  },
  {
    title: "Olive Rust",
    paper: "#F6F4EE",
    ink: "#2E3228",
    muted: "#7D7A6E",
    print: "#D8D6C4",
    motif: "#6B7A4A",
    photo: "#E4E0D4",
  },
  {
    title: "Blush Powder",
    paper: "#F7F2F0",
    ink: "#3A2C30",
    muted: "#8A7578",
    print: "#E8D4D6",
    motif: "#D9A7B0",
    photo: "#EDE4E2",
  },
] as const;

const createDesignSheetSvg = (lookNumber: number) => {
  const theme =
    DESIGN_SHEET_THEMES[
      (((lookNumber - 1) % DESIGN_SHEET_THEMES.length) +
        DESIGN_SHEET_THEMES.length) %
        DESIGN_SHEET_THEMES.length
    ];
  const motif =
    lookNumber % 3 === 0
      ? `<g fill="none" stroke="${theme.motif}" stroke-width="2.2"><circle cx="70" cy="56" r="18"/><circle cx="118" cy="92" r="14"/><circle cx="48" cy="120" r="16"/><circle cx="150" cy="48" r="12"/><circle cx="96" cy="148" r="20"/></g>`
      : lookNumber % 3 === 1
        ? `<g fill="${theme.motif}" opacity="0.78"><ellipse cx="58" cy="40" rx="14" ry="24" transform="rotate(-24 58 40)"/><ellipse cx="118" cy="72" rx="13" ry="22" transform="rotate(18 118 72)"/><ellipse cx="86" cy="128" rx="14" ry="24" transform="rotate(-12 86 128)"/><ellipse cx="154" cy="116" rx="12" ry="20" transform="rotate(22 154 116)"/></g>`
        : `<g fill="none" stroke="${theme.motif}" stroke-width="2"><path d="M20 40c28 8 40 36 12 58-20 16-40 6-36-16 4-20 10-36 24-42Z"/><path d="M92 28c30 6 42 38 10 58-22 14-42 2-36-18 6-20 12-36 26-40Z"/><path d="M150 52c24 10 32 36 6 54-20 12-36 4-32-14 4-18 12-34 26-40Z"/></g>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="980" viewBox="0 0 1600 980">
      <rect width="1600" height="980" fill="${theme.paper}"/>
      <text x="48" y="42" font-family="Georgia, serif" font-size="18" letter-spacing="5" fill="${theme.ink}">FOREVER NEW</text>
      <text x="800" y="42" text-anchor="middle" font-family="Georgia, serif" font-size="20" letter-spacing="4" fill="${theme.ink}">${theme.title.toUpperCase()}</text>
      <text x="1552" y="42" text-anchor="end" font-family="Arial, sans-serif" font-size="13" letter-spacing="2" fill="${theme.muted}">DOCKET &amp; PRINT CAD</text>
      <text x="800" y="68" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" letter-spacing="2" fill="${theme.muted}">LOOK ${lookNumber}  ·  DESIGN SHEET</text>
      <line x1="48" y1="82" x2="1552" y2="82" stroke="#E4DED6"/>
      <rect x="48" y="108" width="300" height="400" fill="${theme.photo}"/>
      <path d="M198 168c-36 8-58 46-52 88 8 56 40 92 52 168 12-76 44-112 52-168 6-42-16-80-52-88Z" fill="${theme.motif}" opacity="0.55"/>
      <text x="64" y="132" font-family="Arial, sans-serif" font-size="12" fill="${theme.ink}">LOOK ${lookNumber}</text>
      <text x="64" y="150" font-family="Arial, sans-serif" font-size="11" fill="${theme.muted}">${theme.title}</text>
      <rect x="368" y="108" width="520" height="400" fill="${theme.print}"/>
      <svg x="368" y="108" width="520" height="400" viewBox="0 0 200 180">${motif}</svg>
      <text x="384" y="132" font-family="Arial, sans-serif" font-size="11" letter-spacing="1.5" fill="${theme.ink}">PRINT CAD — FULL REPEAT</text>
      <rect x="916" y="108" width="300" height="190" fill="#FFFFFF" stroke="#E4DED6"/>
      <path d="M1066 148c-40 6-62 34-58 70 6 48 34 78 58 132 24-54 52-84 58-132 4-36-18-64-58-70Z" fill="${theme.print}" stroke="${theme.motif}" stroke-width="2"/>
      <rect x="1236" y="108" width="316" height="190" fill="#FFFFFF" stroke="#E4DED6"/>
      <path d="M1394 158h96l18 118h-132l18-118Z" fill="${theme.print}" stroke="${theme.motif}" stroke-width="2"/>
      <text x="64" y="540" font-family="Arial, sans-serif" font-size="11" letter-spacing="1.4" fill="${theme.ink}">COLOUR STORY</text>
      <circle cx="80" cy="568" r="12" fill="${theme.photo}" stroke="#D8D2CB"/>
      <circle cx="112" cy="568" r="12" fill="${theme.print}" stroke="#D8D2CB"/>
      <circle cx="144" cy="568" r="12" fill="${theme.motif}" stroke="#D8D2CB"/>
      <circle cx="176" cy="568" r="12" fill="${theme.ink}"/>
      <text x="368" y="540" font-family="Arial, sans-serif" font-size="11" letter-spacing="1.4" fill="${theme.ink}">GARMENT DETAILS</text>
      <text x="368" y="566" font-family="Arial, sans-serif" font-size="12" fill="${theme.muted}">Style  ·  Wrap top  ·  Relaxed fit  ·  Lightweight crepe</text>
      <text x="368" y="590" font-family="Arial, sans-serif" font-size="12" fill="${theme.muted}">Print  ·  ${theme.title}  ·  All-over  ·  Soft placement</text>
      <text x="916" y="540" font-family="Arial, sans-serif" font-size="11" letter-spacing="1.4" fill="${theme.ink}">PRINT DETAILS</text>
      <text x="916" y="566" font-family="Arial, sans-serif" font-size="12" fill="${theme.muted}">Repeat 44 × 64 cm  ·  Watercolour ink  ·  Digital</text>
      <rect x="48" y="630" width="300" height="280" fill="#FFFFFF" stroke="#E4DED6"/>
      <path d="M118 690h60l8 36H110z" fill="none" stroke="${theme.ink}" stroke-width="1.6"/>
      <path d="M218 690h60l8 36h-76z" fill="none" stroke="${theme.ink}" stroke-width="1.6"/>
      <text x="64" y="656" font-family="Arial, sans-serif" font-size="11" fill="${theme.muted}">FLAT SKETCH</text>
      <rect x="368" y="630" width="520" height="280" fill="#FFFFFF" stroke="#E4DED6"/>
      <text x="384" y="656" font-family="Arial, sans-serif" font-size="11" fill="${theme.muted}">COLOUR BREAKDOWN</text>
      <rect x="384" y="680" width="220" height="10" fill="${theme.print}"/>
      <rect x="384" y="704" width="168" height="10" fill="${theme.motif}"/>
      <rect x="384" y="728" width="120" height="10" fill="${theme.photo}"/>
      <rect x="384" y="752" width="88" height="10" fill="${theme.ink}" opacity="0.35"/>
      <rect x="916" y="630" width="636" height="280" fill="#FFFFFF" stroke="#E4DED6"/>
      <text x="932" y="656" font-family="Arial, sans-serif" font-size="11" fill="${theme.muted}">EMBELLISHMENT / NOTES</text>
      <text x="932" y="690" font-family="Arial, sans-serif" font-size="13" fill="${theme.ink}">Self-tie wrap  ·  Soft drape  ·  Print aligned at hem</text>
      <text x="800" y="950" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="${theme.muted}">ORIGINAL DESIGN &amp; PRINT DEVELOPMENT  ·  LOOK ${lookNumber} MOCK SHEET</text>
    </svg>`
  )}`;
};

export const designSheetImageAt = (index: number) =>
  createDesignSheetSvg(index + 1);

export const rangePlanBreakdown = (
  items: RangePlanItem[]
): CollectionBuildRangeBreakdown[] => {
  const quantities = new Map<string, number>();
  items.forEach(item => {
    if (item.quantity <= 0) {
      return;
    }
    quantities.set(
      item.productType,
      (quantities.get(item.productType) || 0) + item.quantity
    );
  });
  return Array.from(quantities, ([productType, quantity]) => ({
    productType,
    quantity,
  }));
};

export const SILHOUETTE_HELPER =
  "One last thing! Before I build your looks tell me the style direction you have in mind.";

/** Select Preferences section helper (Pattern Story tiles). */
export const PREFERENCE_SECTION_HELPER =
  "Got a favourite design from this story, or want me to pick something?";

export type SilhouetteFieldKey =
  | "influence"
  | "styleType"
  | "sleeve"
  | "neckline"
  | "materials"
  | "length"
  | "fit"
  | "wash";

export type SilhouetteFieldContext = {
  category: string;
  market?: string;
  startingDirection?: string;
};

const SILHOUETTE_FIELD_ORDER: SilhouetteFieldKey[] = [
  "influence",
  "styleType",
  "sleeve",
  "neckline",
  "materials",
  "length",
  "fit",
  "wash",
];

export type SilhouetteStyleValues = {
  influence: string;
  styleType: string;
  sleeve: string;
  neckline: string;
  materials: string;
  length: string;
  fit: string;
  wash: string;
  /**
   * Denim Look Details only — single catalog material id.
   * `materials` mirrors the label for display / non-denim compat.
   */
  denimMaterialId: string;
  /**
   * Denim Look Details only — single catalog wash id.
   * `wash` mirrors the label for display / non-denim compat.
   */
  denimWashId: string;
  /**
   * Ordered Print / Embroidery / Graphic selections (selection order =
   * Primary → Secondary → Tertiary). Source of truth for the form.
   */
  garmentFormats: SilhouetteGarmentFormatEntry[];
  /** Primary format mirror for BE / session fallback. */
  garmentFormat: string;
  /** Primary pattern-format mirror. */
  patternFormat: string;
  /** Embroidery stitch when primary format is Embroidery. */
  embroideryType: string;
  /** Per-style free text (Additional Input). */
  additionalPrompt: string;
  uploadFileName: string;
  /** Public CDN/S3 URL for the uploaded silhouette (generation ref). */
  uploadUrl: string;
  selectedSketchIds: string[];
  silhouetteId: string;
  silhouetteName: string;
};

export type SilhouetteGroup = {
  productType: string;
  styles: SilhouetteStyleValues[];
};

export const SILHOUETTE_FIELD_LABELS: Record<SilhouetteFieldKey, string> = {
  influence: "Influence",
  styleType: "Style type",
  sleeve: "Sleeve",
  neckline: "Neckline",
  materials: "Materials",
  length: "Length",
  fit: "Fit",
  wash: "Wash",
};

/** CB Look Details influence values (not IG Generic / New trend). */
export const COLLECTION_BUILD_INFLUENCE_OPTIONS = [
  "from the library",
  "from the forecast",
] as const;

/** Dropdown copy — stored values stay lowercase for session/BE matching. */
export const COLLECTION_BUILD_INFLUENCE_LABELS: Record<
  (typeof COLLECTION_BUILD_INFLUENCE_OPTIONS)[number],
  string
> = {
  "from the library": "From the Library",
  "from the forecast": "From the Forecast",
};

export const collectionBuildInfluenceSelectOptions = (): SelectOption[] =>
  COLLECTION_BUILD_INFLUENCE_OPTIONS.map(value => ({
    value,
    label: COLLECTION_BUILD_INFLUENCE_LABELS[value],
  }));

export const isCollectionBuildLibraryInfluence = (value?: string | null) =>
  normalizeCollectionBuildInfluence(value) === "from the library";

export const isCollectionBuildForecastInfluence = (value?: string | null) =>
  normalizeCollectionBuildInfluence(value) === "from the forecast";

/** Required Look Details dropdowns for the selected Influence. */
export const requiredSilhouetteFieldKeysFor = (
  influence?: string | null
): SilhouetteFieldKey[] => {
  const normalized = normalizeCollectionBuildInfluence(influence);
  if (normalized === "from the library") {
    return ["influence", "styleType", "materials"];
  }
  if (normalized === "from the forecast") {
    return ["influence", "materials"];
  }
  return ["influence"];
};

/** Map legacy IG values so reopened sessions still match the CB dropdown. */
export const normalizeCollectionBuildInfluence = (value?: string | null) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "";
  }
  const lower = trimmed.toLowerCase();
  if (trimmed === LIBRARY_INFLUENCE || lower === "from the library") {
    return "from the library";
  }
  if (trimmed === FORECAST_INFLUENCE || lower === "from the forecast") {
    return "from the forecast";
  }
  return trimmed;
};

export const isRequiredSilhouetteField = (
  field: SilhouetteFieldKey,
  influence?: string | null
) => requiredSilhouetteFieldKeysFor(influence).includes(field);

export const emptySilhouetteStyle = (): SilhouetteStyleValues => ({
  influence: "",
  styleType: "",
  sleeve: "",
  neckline: "",
  materials: "",
  length: "",
  fit: "",
  wash: "",
  denimMaterialId: "",
  denimWashId: "",
  garmentFormats: [],
  garmentFormat: "",
  patternFormat: "",
  embroideryType: "",
  additionalPrompt: "",
  uploadFileName: "",
  uploadUrl: "",
  selectedSketchIds: [],
  silhouetteId: "",
  silhouetteName: "",
});

const parseGarmentFormatRank = (
  value: unknown
): GarmentFormatRank | undefined => {
  const trimmed = String(value || "")
    .trim()
    .toLowerCase();
  return GARMENT_FORMAT_RANKS.find(rank => rank === trimmed);
};

/** Sort by stored rank when present; otherwise keep array order (check order). */
const orderGarmentFormatInputs = (
  entries: SilhouetteGarmentFormatInput[]
): SilhouetteGarmentFormatInput[] => {
  const rankIndex = (entry: SilhouetteGarmentFormatInput) => {
    const rank = parseGarmentFormatRank(entry?.rank);
    return rank ? GARMENT_FORMAT_RANKS.indexOf(rank) : Number.MAX_SAFE_INTEGER;
  };
  const hasAnyRank = entries.some(entry => parseGarmentFormatRank(entry?.rank));
  if (!hasAnyRank) {
    return entries;
  }
  return [...entries].sort((left, right) => {
    const delta = rankIndex(left) - rankIndex(right);
    return delta !== 0 ? delta : 0;
  });
};

/** Build ranked entries from a legacy single garmentFormat (= primary). */
export const garmentFormatsFromLegacy = (
  garmentFormat?: string,
  patternFormat?: string,
  embroideryType?: string
): SilhouetteGarmentFormatEntry[] => {
  const format = String(garmentFormat || "").trim();
  if (!format) {
    return [];
  }
  return [
    {
      format,
      rank: "primary",
      patternFormat: String(patternFormat || "").trim(),
      embroideryType:
        format === "Embroidery" ? String(embroideryType || "").trim() : "",
    },
  ];
};

/**
 * Normalize payload/UI entries and assign ranks by check/array order.
 * Explicit `[]` stays empty (do not resurrect legacy) so uncheck can clear.
 * Uncheck removes that entry; remaining keep relative order and ranks close gaps.
 */
export const normalizeGarmentFormats = (
  entries: SilhouetteGarmentFormatInput[] | null | undefined,
  legacy?: {
    garmentFormat?: string;
    patternFormat?: string;
    embroideryType?: string;
  }
): SilhouetteGarmentFormatEntry[] => {
  const source = Array.isArray(entries)
    ? orderGarmentFormatInputs(entries)
    : garmentFormatsFromLegacy(
        legacy?.garmentFormat,
        legacy?.patternFormat,
        legacy?.embroideryType
      );
  const seen = new Set<string>();
  const next: SilhouetteGarmentFormatEntry[] = [];
  source.forEach(entry => {
    if (next.length >= GARMENT_FORMAT_RANKS.length) {
      return;
    }
    const format = String(entry?.format || "").trim();
    if (
      !format ||
      seen.has(format) ||
      !(COLLECTION_GARMENT_FORMATS as readonly string[]).includes(format)
    ) {
      return;
    }
    seen.add(format);
    const allowedPatterns = getPatternSubtypes(format);
    const rawPattern = String(entry?.patternFormat || "").trim();
    // Drop format-name stubs / values not in the IG pattern-subtype lists.
    const patternFormat = (allowedPatterns as readonly string[]).includes(
      rawPattern
    )
      ? rawPattern
      : "";
    next.push({
      format,
      rank: GARMENT_FORMAT_RANKS[next.length],
      patternFormat,
      embroideryType:
        format === "Embroidery"
          ? String(entry?.embroideryType || "").trim()
          : "",
    });
  });
  return next;
};

/** Keep primary legacy fields in sync with garmentFormats[0]. */
export const withSyncedGarmentFormatFields = (
  style: SilhouetteStyleValues
): SilhouetteStyleValues => {
  // Always trust the array (including []) — never rehydrate from stale mirrors.
  const garmentFormats = normalizeGarmentFormats(style.garmentFormats);
  const primary = garmentFormats[0];
  return {
    ...style,
    garmentFormats,
    garmentFormat: primary?.format || "",
    patternFormat: primary?.patternFormat || "",
    embroideryType:
      primary?.format === "Embroidery" ? primary.embroideryType || "" : "",
    additionalPrompt: String(style.additionalPrompt || "").trim(),
    uploadFileName: String(style.uploadFileName || "").trim(),
    uploadUrl: String(style.uploadUrl || "").trim(),
  };
};

/**
 * Check order = rank. Clicking an already-selected format unchecks it and
 * closes rank gaps for whatever remains.
 */
export const toggleGarmentFormatSelection = (
  current: SilhouetteGarmentFormatEntry[],
  format: string,
  checked?: boolean
): SilhouetteGarmentFormatEntry[] => {
  const trimmed = String(format || "").trim();
  if (!trimmed) {
    return normalizeGarmentFormats(current);
  }
  const isSelected = current.some(entry => entry.format === trimmed);
  // Prefer membership when `checked` is omitted; honor explicit checkbox value.
  const shouldSelect = typeof checked === "boolean" ? checked : !isSelected;
  if (shouldSelect) {
    if (isSelected) {
      return normalizeGarmentFormats(current);
    }
    if (current.length >= GARMENT_FORMAT_RANKS.length) {
      return normalizeGarmentFormats(current);
    }
    return normalizeGarmentFormats([
      ...current,
      {
        format: trimmed,
        rank: GARMENT_FORMAT_RANKS[current.length],
        patternFormat: "",
        embroideryType: "",
      },
    ]);
  }
  return normalizeGarmentFormats(
    current.filter(entry => entry.format !== trimmed)
  );
};

export const updateGarmentFormatEntry = (
  current: SilhouetteGarmentFormatEntry[],
  format: string,
  patch: Partial<
    Pick<SilhouetteGarmentFormatEntry, "patternFormat" | "embroideryType">
  >
): SilhouetteGarmentFormatEntry[] =>
  normalizeGarmentFormats(
    current.map(entry =>
      entry.format === format
        ? {
            ...entry,
            ...patch,
            embroideryType:
              entry.format === "Embroidery" ||
              patch.embroideryType !== undefined
                ? String(
                    patch.embroideryType !== undefined
                      ? patch.embroideryType
                      : entry.embroideryType || ""
                  ).trim()
                : "",
          }
        : entry
    )
  );

export const garmentFormatRankLabel = (rank: GarmentFormatRank) =>
  GARMENT_FORMAT_RANK_LABELS[rank];

export const garmentFormatEntryLabel = (entry: SilhouetteGarmentFormatEntry) =>
  `${entry.format} (${garmentFormatRankLabel(entry.rank)})`;

/**
 * Closed-field label: ranked selection summary when any format is checked;
 * muted placeholder only when empty.
 */
export const garmentFormatSelectSummary = (
  entries: SilhouetteGarmentFormatEntry[]
) => {
  if (!entries.length) {
    return GARMENT_FORMAT_SELECT_PLACEHOLDER;
  }
  return [...entries]
    .sort(
      (a, b) =>
        GARMENT_FORMAT_RANKS.indexOf(a.rank) -
        GARMENT_FORMAT_RANKS.indexOf(b.rank)
    )
    .map(garmentFormatEntryLabel)
    .join(", ");
};

export const silhouetteCatalogCategory = (category: string) =>
  /ethnic/i.test(category) ? "Ethnic RTW" : "Western RTW";

export const silhouetteCatalogDirection = (startingDirection?: string) =>
  /denim/i.test(startingDirection || "") ? "denim" : undefined;

export const isRequiredSilhouetteFieldForContext = (
  field: SilhouetteFieldKey,
  influence?: string | null,
  context?: SilhouetteFieldContext
) => {
  if (isRequiredSilhouetteField(field, influence)) {
    return true;
  }
  // Denim Look Details: washes are required alongside materials.
  return (
    field === "wash" &&
    silhouetteCatalogDirection(context?.startingDirection) === "denim" &&
    Boolean(normalizeCollectionBuildInfluence(influence))
  );
};

export const silhouetteFieldsFor = (
  productType: string,
  context: SilhouetteFieldContext
): SilhouetteFieldKey[] => {
  const isDenim =
    silhouetteCatalogDirection(context.startingDirection) === "denim";
  // Denim CB Look Details owns materials/washes with catalog swatches.
  // Pass undefined direction into follow-ups so product sleeve/neckline/etc.
  // still appear; then force materials + wash (IG denim path strips them).
  const followUps = getFollowUpFields(
    context.market || "",
    silhouetteCatalogCategory(context.category),
    productType,
    undefined,
    isDenim ? undefined : silhouetteCatalogDirection(context.startingDirection)
  );
  const allowed = new Set<SilhouetteFieldKey>(["influence", "styleType"]);
  followUps.forEach(field => {
    allowed.add(field === "material" ? "materials" : field);
  });
  if (isDenim) {
    allowed.add("materials");
    allowed.add("wash");
  }
  return SILHOUETTE_FIELD_ORDER.filter(field => allowed.has(field));
};

export const lengthOptionsForProduct = (productType: string): string[] =>
  getLengthOptions(productType);

export const createSilhouetteGroups = (
  breakdown: CollectionBuildRangeBreakdown[]
): SilhouetteGroup[] =>
  breakdown
    .filter(item => item.quantity > 0)
    .map(item => ({
      productType: item.productType,
      styles: Array.from({ length: item.quantity }, emptySilhouetteStyle),
    }));

/**
 * Merge persisted look payloads into range-plan silhouette groups (reopen/hydrate).
 * Matches by productType + styleIndex (falling back to order within product).
 */
export type SilhouetteLookSeed = {
  productType?: string;
  styleIndex?: number;
  influence?: string;
  styleType?: string;
  material?: string;
  materials?: string;
  sleeve?: string;
  neckline?: string;
  fit?: string;
  wash?: string;
  length?: string;
  denimMaterialId?: string;
  denimWashId?: string;
  /** @deprecated Hydrate fallback from earlier multi-select drafts. */
  denimMaterialIds?: string[];
  /** @deprecated Hydrate fallback from earlier multi-select drafts. */
  denimWashIds?: string[];
  garmentFormats?: SilhouetteGarmentFormatInput[];
  garmentFormat?: string;
  patternFormat?: string;
  embroideryType?: string;
  silhouetteId?: string;
  silhouetteName?: string;
  selectedSketchStems?: string[];
  sketchUploadName?: string;
  /** Uploaded silhouette image URL for look generation. */
  sketchUploadUrl?: string;
  additionalPrompt?: string;
};

/** Resolve a denim catalog label for a single id. */
export const denimCatalogLabelForId = (
  id: string | null | undefined,
  catalog: Array<{ id: string; label: string }> | null | undefined
): string => {
  const trimmed = String(id || "").trim();
  if (!trimmed) {
    return "";
  }
  return (catalog || []).find(item => item.id === trimmed)?.label || trimmed;
};

/** Session moodboard still needs one material/wash — take the first complete style. */
export const denimSessionIdsFromGroups = (
  groups: SilhouetteGroup[]
): { denimMaterialId: string; denimWashId: string } => {
  for (const group of groups) {
    for (const style of group.styles) {
      const denimMaterialId = String(style.denimMaterialId || "").trim();
      const denimWashId = String(style.denimWashId || "").trim();
      if (denimMaterialId && denimWashId) {
        return { denimMaterialId, denimWashId };
      }
    }
  }
  let denimMaterialId = "";
  let denimWashId = "";
  for (const group of groups) {
    for (const style of group.styles) {
      if (!denimMaterialId) {
        denimMaterialId = String(style.denimMaterialId || "").trim();
      }
      if (!denimWashId) {
        denimWashId = String(style.denimWashId || "").trim();
      }
      if (denimMaterialId && denimWashId) {
        return { denimMaterialId, denimWashId };
      }
    }
  }
  return { denimMaterialId, denimWashId };
};

const firstDenimId = (
  single?: string | null,
  legacyIds?: string[] | null
): string => {
  const fromSingle = String(single || "").trim();
  if (fromSingle) {
    return fromSingle;
  }
  if (!Array.isArray(legacyIds)) {
    return "";
  }
  return legacyIds.map(id => String(id || "").trim()).find(Boolean) || "";
};

/** First style with a garment format — session-level BE fallback. */
export const garmentFormatFallbackFromGroups = (
  groups: SilhouetteGroup[]
): {
  garmentFormat: string;
  patternFormat: string;
  embroideryType: string;
} => {
  for (const group of groups) {
    for (const style of group.styles) {
      const synced = withSyncedGarmentFormatFields(style);
      const garmentFormat = String(synced.garmentFormat || "").trim();
      if (!garmentFormat) {
        continue;
      }
      return {
        garmentFormat,
        patternFormat: String(synced.patternFormat || "").trim(),
        embroideryType:
          garmentFormat === "Embroidery"
            ? String(synced.embroideryType || "").trim()
            : "",
      };
    }
  }
  return { garmentFormat: "", patternFormat: "", embroideryType: "" };
};

export const isStyleGarmentFormatComplete = (
  style: SilhouetteStyleValues,
  requireGarmentFormat: boolean
) => {
  if (!requireGarmentFormat) {
    return true;
  }
  const garmentFormats = normalizeGarmentFormats(style.garmentFormats, {
    garmentFormat: style.garmentFormat,
    patternFormat: style.patternFormat,
    embroideryType: style.embroideryType,
  });
  if (!garmentFormats.length) {
    return false;
  }
  return garmentFormats.every(entry => {
    if (!String(entry.patternFormat || "").trim()) {
      return false;
    }
    if (
      entry.format === "Embroidery" &&
      !String(entry.embroideryType || "").trim()
    ) {
      return false;
    }
    return true;
  });
};

export const areSilhouetteGarmentFormatsComplete = (
  groups: SilhouetteGroup[],
  requireGarmentFormat: boolean
) =>
  !requireGarmentFormat ||
  (groups.length > 0 &&
    groups.every(
      group =>
        group.styles.length > 0 &&
        group.styles.every(style =>
          isStyleGarmentFormatComplete(style, requireGarmentFormat)
        )
    ));

export const applyLooksToSilhouetteGroups = (
  groups: SilhouetteGroup[],
  looks: SilhouetteLookSeed[] | null | undefined,
  /** Legacy session-level values when looks omit per-style garment format. */
  sessionGarmentFallback?: {
    garmentFormat?: string;
    patternFormat?: string;
    embroideryType?: string;
  } | null
): SilhouetteGroup[] => {
  const fallbackFormat = String(
    sessionGarmentFallback?.garmentFormat || ""
  ).trim();
  const fallbackPattern = String(
    sessionGarmentFallback?.patternFormat || ""
  ).trim();
  const fallbackEmbroidery = String(
    sessionGarmentFallback?.embroideryType || ""
  ).trim();
  const applySessionFallback = (style: SilhouetteStyleValues) => {
    if (style.garmentFormats?.length || style.garmentFormat?.trim()) {
      return withSyncedGarmentFormatFields(style);
    }
    if (!fallbackFormat) {
      return withSyncedGarmentFormatFields(style);
    }
    return withSyncedGarmentFormatFields({
      ...style,
      garmentFormats: garmentFormatsFromLegacy(
        fallbackFormat,
        fallbackPattern,
        fallbackEmbroidery
      ),
    });
  };
  if (!looks?.length || !groups.length) {
    if (!fallbackFormat || !groups.length) {
      return groups.map(group => ({
        ...group,
        styles: group.styles.map(style => withSyncedGarmentFormatFields(style)),
      }));
    }
    return groups.map(group => ({
      ...group,
      styles: group.styles.map(applySessionFallback),
    }));
  }
  const byProduct = new Map<string, typeof looks>();
  looks.forEach(look => {
    const productType = String(look.productType || "").trim();
    if (!productType) {
      return;
    }
    const list = byProduct.get(productType) || [];
    list.push(look);
    byProduct.set(productType, list);
  });
  return groups.map(group => {
    const productLooks = byProduct.get(group.productType) || [];
    if (!productLooks.length) {
      return {
        ...group,
        styles: group.styles.map(applySessionFallback),
      };
    }
    const used = new Set<number>();
    return {
      ...group,
      styles: group.styles.map((style, index) => {
        const byIndex = productLooks.find(
          (look, lookIndex) =>
            !used.has(lookIndex) &&
            (look.styleIndex === index ||
              (look.styleIndex == null && lookIndex === index))
        );
        const lookIndex = byIndex
          ? productLooks.indexOf(byIndex)
          : productLooks.findIndex((_, i) => !used.has(i));
        if (lookIndex < 0) {
          return applySessionFallback(style);
        }
        used.add(lookIndex);
        const look = productLooks[lookIndex];
        const stems = Array.isArray(look.selectedSketchStems)
          ? look.selectedSketchStems
              .map(stem => String(stem || "").trim())
              .filter(Boolean)
          : [];
        const garmentFormats = normalizeGarmentFormats(look.garmentFormats, {
          garmentFormat:
            look.garmentFormat || fallbackFormat || style.garmentFormat || "",
          patternFormat:
            look.patternFormat ||
            (look.garmentFormat || fallbackFormat ? fallbackPattern : "") ||
            style.patternFormat ||
            "",
          embroideryType:
            look.embroideryType || fallbackEmbroidery || style.embroideryType,
        });
        const denimMaterialId =
          firstDenimId(look.denimMaterialId, look.denimMaterialIds) ||
          String(style.denimMaterialId || "").trim();
        const denimWashId =
          firstDenimId(look.denimWashId, look.denimWashIds) ||
          String(style.denimWashId || "").trim();
        return withSyncedGarmentFormatFields({
          ...style,
          influence: normalizeCollectionBuildInfluence(
            look.influence || style.influence
          ),
          styleType: String(look.styleType || style.styleType || "").trim(),
          materials: String(
            look.materials || look.material || style.materials || ""
          ).trim(),
          sleeve: String(look.sleeve || style.sleeve || "").trim(),
          neckline: String(look.neckline || style.neckline || "").trim(),
          fit: String(look.fit || style.fit || "").trim(),
          wash: String(look.wash || style.wash || "").trim(),
          length: String(look.length || style.length || "").trim(),
          denimMaterialId,
          denimWashId,
          garmentFormats,
          silhouetteId: String(
            look.silhouetteId || stems[0] || style.silhouetteId || ""
          ).trim(),
          silhouetteName: String(
            look.silhouetteName || style.silhouetteName || ""
          ).trim(),
          selectedSketchIds: stems.length ? stems : style.selectedSketchIds,
          uploadFileName: String(
            look.sketchUploadName || style.uploadFileName || ""
          ).trim(),
          uploadUrl: String(
            look.sketchUploadUrl || style.uploadUrl || ""
          ).trim(),
          additionalPrompt: String(
            look.additionalPrompt || style.additionalPrompt || ""
          ).trim(),
        });
      }),
    };
  });
};

/** Influence plus path fields; denim uses catalog material/wash single-select. */
export const isSilhouetteProductDetailsComplete = (
  style: SilhouetteStyleValues,
  productType: string,
  context: SilhouetteFieldContext,
  requireGarmentFormat: boolean
) => {
  const visible = new Set(silhouetteFieldsFor(productType, context));
  const isDenim =
    silhouetteCatalogDirection(context.startingDirection) === "denim";
  const requiredKeys = [
    ...requiredSilhouetteFieldKeysFor(style.influence),
    ...(isDenim && visible.has("wash")
      ? (["wash"] as SilhouetteFieldKey[])
      : []),
  ];
  const requiredFilled = requiredKeys.every(field => {
    if (field === "materials" && !visible.has("materials")) {
      return true;
    }
    if (field === "wash" && !visible.has("wash")) {
      return true;
    }
    if (isDenim && field === "materials") {
      return Boolean(String(style.denimMaterialId || "").trim());
    }
    if (isDenim && field === "wash") {
      return Boolean(String(style.denimWashId || "").trim());
    }
    const raw =
      field === "influence"
        ? normalizeCollectionBuildInfluence(style.influence)
        : style[field];
    return Boolean(String(raw || "").trim());
  });
  return (
    requiredFilled && isStyleGarmentFormatComplete(style, requireGarmentFormat)
  );
};

export const areSilhouetteProductDetailsComplete = (
  groups: SilhouetteGroup[],
  context: SilhouetteFieldContext,
  requireGarmentFormat: boolean
) =>
  groups.length > 0 &&
  groups.every(
    group =>
      group.styles.length > 0 &&
      group.styles.every(style =>
        isSilhouetteProductDetailsComplete(
          style,
          group.productType,
          context,
          requireGarmentFormat
        )
      )
  );

export const isSilhouetteStyleComplete = (
  style: SilhouetteStyleValues,
  productType: string,
  context: SilhouetteFieldContext
) => {
  // Print / Embroidery is optional (BE Joi allows empty; assemble defaults).
  // Choose ICH Sketch and image upload stay optional.
  return isSilhouetteProductDetailsComplete(style, productType, context, false);
};

export const areSilhouetteGroupsComplete = (
  groups: SilhouetteGroup[],
  context: SilhouetteFieldContext
) =>
  groups.length > 0 &&
  groups.every(
    group =>
      group.styles.length > 0 &&
      group.styles.every(style =>
        isSilhouetteStyleComplete(style, group.productType, context)
      )
  );

/** Filename set but public URL missing (mid/failed upload) — block generate. */
export const hasIncompleteSilhouetteUpload = (
  groups: SilhouetteGroup[] | null | undefined
): boolean =>
  Boolean(
    groups?.some(group =>
      group.styles.some(style => {
        const name = String(style.uploadFileName ?? "").trim();
        const url = String(style.uploadUrl ?? "").trim();
        return Boolean(name) && !url;
      })
    )
  );

export const INCOMPLETE_SILHOUETTE_UPLOAD_MESSAGE =
  "Silhouette image is still uploading or failed to upload. Wait for upload to finish, or clear it and try again.";

export const createLooksFromGroups = (
  groups: SilhouetteGroup[]
): CollectionBuildLook[] => {
  const looks: CollectionBuildLook[] = [];
  groups.forEach(group => {
    group.styles.forEach((_, styleIndex) => {
      const number = looks.length + 1;
      const pair = lookImagePairAt(number - 1);
      looks.push({
        id: `look-${group.productType}-${styleIndex}`,
        number,
        productType: group.productType,
        styleIndex,
        versions: [
          {
            id: `look-${group.productType}-${styleIndex}-1`,
            frontUrl: pair.front,
            backUrl: pair.back,
          },
        ],
      });
    });
  });
  return looks;
};

export const silhouetteProductPillLabel = (
  productType: string,
  quantity: number
) => `${quantity} ${productType}`;

export const collectionBuildDividerClassName = "border-[#E2E2E2]";
/**
 * Exact copies from ImageGeneratorControls — do not reinterpret.
 */
export const sectionTitleClassName = "font-semibold text-secondary";
export const fieldLabelClassName =
  "block text-secondary text-sm font-normal mb-1";
/** Exact IG `formFieldsShellClassName`. */
export const formFieldsShellClassName = "w-full max-w-[1132px]";
/** @deprecated Prefer formFieldsShellClassName (IG-aligned). */
export const collectionBuildFormContentClassName = formFieldsShellClassName;
/**
 * CB Select Inputs width (214). IG uses 207 — keep CB width; do not stretch.
 */
export const FIELD_CONTROL_WIDTH_CLASS =
  "w-full min-w-0 max-w-[214px] sm:w-[214px] sm:shrink-0";
/** Exact IG `fieldsRowClassName`. */
export const fieldsRowClassName = "flex flex-wrap gap-x-6 gap-y-4";
/** Exact IG `sectionClassName`. */
export const sectionBlockClassName = "border-b border-[#E2E2E2] pt-2 pb-6";
/** IG helper under titles: `${helperTextClassName} mt-1 mb-3`. */
export const sectionHelperClassName = `${helperTextClassName} mt-1 mb-3`;
/** View report toggle — same chrome as CollectionBuildDirections. */
export const viewReportButtonClassName = `mt-3 h-9 rounded-lg border ${collectionBuildBorderClassName} bg-white px-4 font-avenir text-sm font-medium text-secondary transition-colors hover:bg-brandPrimarySubtle focus:outline-none`;

/**
 * Style form column width: fixed so the product card can shrink-wrap
 * (`w-fit`) around 1–2 styles. Extra styles keep the same basis and scroll
 * inside `max-w-full` (card = shell width).
 */
export const SILHOUETTE_STYLE_COLUMN_CLASS =
  "box-border w-[480px] min-w-[480px] max-w-[480px] shrink-0";
export const exclusivePillClassName = (active: boolean, disabled = false) =>
  `inline-flex h-8 items-center rounded-full border ${collectionBuildBorderClassName} px-4 text-sm font-medium font-avenir focus:outline-none ${
    disabled
      ? "cursor-not-allowed bg-white text-secondary opacity-40"
      : active
        ? "bg-[#DFD6CD] text-primary cursor-pointer"
        : "bg-white text-secondary hover:bg-lightGray cursor-pointer"
  }`;
/** Micro-theme multi-select: active (viewing prefs) = Brand Primary Subtle; selected = filled. */
export const microThemeSwitcherPillClassName = (
  active: boolean,
  selected = false,
  disabled = false
) =>
  `inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-full border ${collectionBuildBorderClassName} px-4 text-sm font-medium font-avenir focus:outline-none ${
    disabled
      ? "cursor-not-allowed bg-white text-secondary opacity-40"
      : active
        ? "bg-brandPrimarySubtle text-primary cursor-pointer"
        : selected
          ? "bg-[#DFD6CD] text-primary cursor-pointer"
          : "bg-white text-secondary hover:bg-lightGray cursor-pointer"
  }`;
export const paletteColumnClassName =
  "flex w-fit max-w-full min-w-0 flex-col items-stretch";
export const paletteChipClassName = (active: boolean) =>
  `inline-flex w-fit max-w-full cursor-pointer items-center justify-center gap-2 overflow-x-auto rounded-full border bg-white px-2.5 py-1.5 focus:outline-none ${
    active
      ? `${collectionBuildBorderClassName} ring-2 ring-[#737373]`
      : `${collectionBuildDividerClassName} hover:border-[#737373]`
  }`;
export const paletteActionPillClassName = `inline-flex h-8 cursor-pointer items-center whitespace-nowrap rounded-full border ${collectionBuildBorderClassName} bg-white px-4 font-avenir text-sm font-medium text-secondary hover:bg-lightGray focus:outline-none`;
/** Fixed tile width so preference grids do not stretch on wide screens. */
export const preferenceGridClassName =
  "grid w-fit min-w-0 max-w-full grid-cols-5 content-start gap-1.5 [&>*]:w-[10.5rem]";
export const preferenceRecommendedClassName = "w-[10.5rem] max-w-full";
/** Denim material/wash tiles — same left-aligned wrap rhythm as denim colour swatches. */
export const denimTileGridClassName =
  "flex w-fit min-w-0 max-w-full flex-wrap items-start gap-x-4 gap-y-3";
export const denimTileCellClassName = "w-[10.5rem] max-w-full shrink-0";
export const paletteSwatchClassName = `relative h-6 w-6 shrink-0 rounded-full border ${collectionBuildBorderClassName} shadow-inner`;
/** One consistent size for all CB palette capsule dots (ignore major/minor). */
export const paletteTrackDotClassName =
  "h-6 w-6 shrink-0 rounded-full shadow-[0_0_0_1px_#E2E2E2]";
/** @deprecated CB uses uniform dots; kept for any leftover imports. */
export const paletteTrackDotMinorClassName = paletteTrackDotClassName;
export const paletteLabelClassName =
  "mt-1.5 w-0 min-w-full text-left text-[11px] font-normal uppercase leading-[14px] text-secondary";
/**
 * Exact IG page wrapper (sidebar width composed in CollectionBuild.tsx).
 * IG: fixed … px-4 pt-1 pb-4 font-poppins bg-white text-secondary
 */
export const pageShellClassName =
  "fixed transition-all duration-500 ease-in-out overflow-y-auto overflow-x-hidden h-[calc(100vh-80px)] ml-1 px-4 pt-1 pb-4 font-poppins bg-white text-secondary";

export const scrollCollectionSectionIntoView = (
  element: HTMLElement | null,
  block: ScrollLogicalPosition = "nearest"
) => {
  if (!element) {
    return;
  }
  const scroller = element.closest<HTMLElement>(
    ".overflow-y-auto, .overflow-y-scroll"
  );
  if (!scroller) {
    element.scrollIntoView({ behavior: "smooth", block });
    return;
  }
  const elementRect = element.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const overflowTop = scrollerRect.top - elementRect.top;
  const overflowBottom = elementRect.bottom - scrollerRect.bottom;
  /** Skip tiny corrections so smooth scroll does not fight the user. */
  const ALIGN_EPSILON_PX = 12;
  if (block === "nearest") {
    if (overflowTop <= 0 && overflowBottom <= 0) {
      return;
    }
    if (overflowBottom > 0 && overflowTop <= 0) {
      scroller.scrollTo({
        top: scroller.scrollTop + overflowBottom,
        behavior: "smooth",
      });
      return;
    }
  }
  if (Math.abs(overflowTop) <= ALIGN_EPSILON_PX) {
    return;
  }
  scroller.scrollTo({
    top: Math.max(0, scroller.scrollTop - overflowTop),
    behavior: "smooth",
  });
};

export const submitButtonClassName =
  "h-[46px] rounded-full bg-primary px-8 font-avenir text-sm text-inverted transition-colors hover:bg-black focus:outline-none disabled:cursor-not-allowed disabled:opacity-50";
export const outlineButtonClassName = `h-[46px] rounded-full border ${collectionBuildBorderClassName} bg-white px-8 font-avenir text-sm text-secondary transition-colors hover:bg-lightGray focus:outline-none`;
export const mutedButtonClassName =
  "h-[46px] rounded-full bg-gray px-8 font-avenir text-sm text-secondary transition-colors hover:bg-lightGray focus:outline-none";
export const textareaClassName = `min-h-[99px] w-full max-w-full resize-y rounded-lg border ${collectionBuildBorderClassName} bg-white px-4 py-3 font-avenir text-sm text-secondary focus:border-secondary focus:outline-none`;
export const hexInputClassName = `h-8 w-full rounded-lg border ${collectionBuildBorderClassName} bg-white px-3 font-avenir text-xs text-secondary focus:outline-none`;

export const productOptionsFor = (market: string, category: string) =>
  PRODUCT_TYPES[market]?.[category] || [];

/** Pattern-format subtypes from IG dictionaries (not the format name itself). */
export const patternFormatsForGarment = (garmentFormat: string) =>
  getPatternSubtypes(garmentFormat);

export const toSelectOptions = (values: readonly string[]): SelectOption[] =>
  values.map(value => ({ value, label: value }));

export const createRangePlanItem = (
  productType: string,
  quantity = DEFAULT_QUANTITY
): RangePlanItem => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  productType,
  quantity,
});

export const createCustomPalette = (
  colors: string[],
  customCount: number
): ColorPaletteOption => ({
  id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  label: customCount === 0 ? "Custom" : `Custom ${customCount + 1}`,
  colors,
});

export const countCustomPalettes = (palettes: ColorPaletteOption[]) =>
  palettes.filter(palette => palette.id.startsWith("custom-")).length;

export const sumRangePlanQuantity = (items: RangePlanItem[]) =>
  items.reduce((sum, item) => sum + item.quantity, 0);

export const getRangePlanSlots = (
  items: RangePlanItem[],
  canAdd: boolean
): RangePlanSlot[] => (canAdd ? [...items, ADD_SLOT] : [...items]);

export const getRangePlanPageCount = (slotCount: number) =>
  Math.max(1, Math.ceil(slotCount / RANGE_PLAN_PAGE_SIZE));

export const sliceRangePlanPage = (slots: RangePlanSlot[], page: number) => {
  const start = page * RANGE_PLAN_PAGE_SIZE;
  return slots.slice(start, start + RANGE_PLAN_PAGE_SIZE);
};

export const padRangePlanPage = (
  visible: RangePlanSlot[],
  paginated: boolean
): RangePlanDisplaySlot[] => {
  if (!paginated) {
    return visible;
  }
  const padded: RangePlanDisplaySlot[] = [...visible];
  while (padded.length < RANGE_PLAN_PAGE_SIZE) {
    padded.push(EMPTY_SLOT);
  }
  return padded;
};

export const getLookBookPageCount = (lookCount: number) =>
  Math.max(1, Math.ceil(Math.max(0, lookCount) / LOOK_BOOK_PAGE_SIZE));

/** 1-based Look Book page that contains a 0-based look index. */
export const getLookBookPageForIndex = (index: number) =>
  Math.max(1, Math.floor(Math.max(0, index) / LOOK_BOOK_PAGE_SIZE) + 1);

export const sliceLookBookPage = <T>(
  items: readonly T[],
  page: number
): T[] => {
  const start = (Math.max(1, page) - 1) * LOOK_BOOK_PAGE_SIZE;
  return items.slice(start, start + LOOK_BOOK_PAGE_SIZE);
};

/** Ensure Look 1..N labels match array order (global, not page-local). */
export const withSequentialLookNumbers = (
  items: readonly CollectionBuildLook[]
): CollectionBuildLook[] =>
  items.map((look, index) =>
    look.number === index + 1 ? look : { ...look, number: index + 1 }
  );
